import { useTranslation } from "react-i18next";
import { LogOut, UserCheck } from "lucide-react";
import { LangToggle, ThemeToggle } from "../components/controls/Toggles";
import { NeuButton, NeuCard } from "../components/neu/Neu";
import { useAuth } from "../context/AuthContext";
import { countryRule, useCountries } from "../features/admin/useCountries";
import { hasErrors, personalErrors } from "../features/admin/idValidate";
import { ProfileFields } from "../features/profile/ProfileFields";
import { useProfileWizard } from "../features/profile/useProfileWizard";

export default function CompleteProfilePage() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const wizard = useProfileWizard();
  const countries = useCountries();
  const rule = countryRule(countries, wizard.personal.nationality);
  const errors = personalErrors(wizard.personal, rule);

  return (
    <div className="flex h-[100dvh] flex-col overflow-hidden bg-background">
      {/* رأس ثابت أعلى الشاشة — يحترم المنطقة الآمنة للهاتف ولا يتحرك مع التمرير */}
      <header
        data-testid="profile-header"
        className="sticky top-0 z-30 shrink-0 border-b border-border/40 bg-background/95 px-5 pb-4 pt-[max(1.25rem,env(safe-area-inset-top))] backdrop-blur"
      >
        <div className="mx-auto flex max-w-3xl items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="neu flex h-12 w-12 items-center justify-center rounded-2xl p-0">
              <UserCheck className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-xl font-black" data-testid="profile-title">{t("profile.title")}</h1>
              <p className="text-xs text-muted-foreground">{t("profile.subtitle")}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <LangToggle />
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* المحتوى القابل للتمرير فقط */}
      <main className="detail-zoom flex-1 overflow-y-auto px-5 pb-[max(2.5rem,env(safe-area-inset-bottom))] pt-6">
        <div className="mx-auto max-w-3xl">
          <NeuCard data-testid="profile-card">
            {wizard.loading ? (
              <p className="text-sm text-muted-foreground">{t("app.loading")}</p>
            ) : (
              <>
                <p className="mb-5 text-xs font-semibold text-warning" data-testid="profile-missing-count">
                  {t("profile.missingCount", { count: wizard.missing.length })}
                </p>
                <ProfileFields
                  phone={wizard.phone}
                  setPhone={wizard.setPhone}
                  personal={wizard.personal}
                  setPersonal={wizard.setPersonal}
                  countries={countries}
                  rule={rule}
                  errors={errors}
                />
                <div className="mt-6 flex flex-wrap gap-2">
                  <NeuButton
                    variant="primary"
                    disabled={wizard.busy || hasErrors(errors)}
                    data-testid="profile-save"
                    onClick={wizard.save}
                  >
                    {t("profile.save")}
                  </NeuButton>
                  <NeuButton className="flex items-center gap-2" data-testid="profile-logout" onClick={signOut}>
                    <LogOut className="h-4 w-4" /> {t("app.logout")}
                  </NeuButton>
                </div>
              </>
            )}
          </NeuCard>
        </div>
      </main>
    </div>
  );
}
